import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  type UsePrivateWorkspaceAgentSessionElicitationRespondMutationParams,
  usePrivateWorkspaceAgentSessionElicitationRespondMutation,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/mutations/usePrivateWorkspaceAgentSessionElicitationRespondMutation';
import {
  getPrivateWorkspaceAgentSessionGetKey,
  usePrivateWorkspaceAgentSessionGet,
} from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentSessionGet';
import {
  classifyElicitation,
  type ParsedElicitation,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/elicitation';

export type RespondToElicitationParams = {
  elicitationId: string;
  customText?: string;
  fieldValues?: Array<{ label: string; value: string }>;
  modifiedArguments?: Record<string, unknown>;
  numberValue?: number;
  rememberForSession?: boolean;
  response: 'approved' | 'denied';
  selectedIndexes?: number[];
};

type ElicitationRespondRequest = UsePrivateWorkspaceAgentSessionElicitationRespondMutationParams & {
  customText?: string;
  fieldValues?: Array<{ label: string; value: string }>;
  numberValue?: number;
  selectedIndexes?: number[];
};

export type UsePrivateWorkspaceAgentElicitationsParams = {
  /** Read at call time, so a response is filed against the session live when the user answered. */
  getActiveSessionId: () => string | null;
  /**
   * Assistant messages already on screen when the user answers, so a later remount can still
   * tell a new reply from the question.
   */
  getMessagesSnapshot: () => readonly { id: string; role: string }[];
  onError: (message: string) => void;
  /**
   * Session GET lives in this hook, not the session owner. The owner still has to drop the selected
   * id and show a banner, so it supplies this rather than duplicating the query.
   */
  onSessionLoadError: (error: unknown) => void;
  sessionId: string | null;
  workspaceId: string;
};

export type UsePrivateWorkspaceAgentElicitationsResult = {
  appendParsedElicitation: (elicitation: ParsedElicitation) => void;
  /** Drops the client loader once the thread is showing the server's next activity. */
  clearElicitationFollowUp: () => void;
  /** The session was still running or still waiting on input after this answer. */
  noteElicitationFollowUpSawSessionActivity: () => void;
  clearElicitations: () => void;
  /**
   * Seed for the consent dock's "Always allow in this thread" box — keyed by backend `toolName`
   * (`sessionToolConsent`). Defaults off for each tool; an opt-in on Approve sticks for that tool only.
   */
  getConsentRememberForSession: (toolName: string) => boolean;
  /**
   * Assistant message ids on screen when the user answered. Null once the follow-up loader stands down.
   * Lives with the session, so remounting the message list cannot recapture a reply that already arrived.
   */
  elicitationFollowUpAssistantMessageIds: ReadonlySet<string> | null;
  /** True after this answer has seen the session still running or still waiting on input. */
  elicitationFollowUpSawSessionActivity: boolean;
  /**
   * True from the answer click — the dock unmounts immediately — until the server's next
   * activity is on screen. The run line shows a loader across that gap.
   */
  isAwaitingElicitationFollowUp: boolean;
  pendingElicitations: ParsedElicitation[];
  /** Re-reads the session's prompts from the server, for when the socket has missed some. */
  refreshPendingElicitations: () => void;
  removeElicitation: (elicitationId: string) => void;
  respondToElicitation: (params: RespondToElicitationParams) => Promise<void>;
  setConsentRememberForSession: (toolName: string, value: boolean) => void;
};

const DEFAULT_CONSENT_REMEMBER_FOR_SESSION = false;

const RESPOND_ERROR_MESSAGE = 'Could not send your response to the agent.';

const getErrorMessage = (error: unknown) => (error instanceof Error ? error.message : RESPOND_ERROR_MESSAGE);

/** Tracks the prompts the agent is blocked on, and answers them. */
export const usePrivateWorkspaceAgentElicitations = ({
  getActiveSessionId,
  getMessagesSnapshot,
  onError,
  onSessionLoadError,
  sessionId,
  workspaceId,
}: UsePrivateWorkspaceAgentElicitationsParams): UsePrivateWorkspaceAgentElicitationsResult => {
  const [pendingElicitations, setPendingElicitations] = useState<ParsedElicitation[]>([]);
  const [elicitationFollowUp, setElicitationFollowUp] = useState<{
    assistantMessageIds: ReadonlySet<string>;
    sawSessionActivity: boolean;
  } | null>(null);
  const [followUpSessionId, setFollowUpSessionId] = useState(sessionId);
  const isAwaitingElicitationFollowUp = elicitationFollowUp !== null;

  if (followUpSessionId !== sessionId) {
    setFollowUpSessionId(sessionId);
    setElicitationFollowUp(null);
  }

  const consentRememberForSessionByToolNameRef = useRef<Record<string, boolean>>({});
  const pendingElicitationIdsRef = useRef<Set<string>>(new Set());
  const { mutateAsync: respondToElicitationRequest } = usePrivateWorkspaceAgentSessionElicitationRespondMutation();
  const queryClient = useQueryClient();

  const sessionQuery = usePrivateWorkspaceAgentSessionGet({ sessionId: sessionId ?? undefined, workspaceId });

  /**
   * Every prompt answered while the panel has been open. The session payload keeps listing a prompt
   * as pending until the backend records the answer, and that payload seeds this list whenever it is
   * refetched — so without a memory of what has been dealt with, a card the user already approved
   * comes back. Not cleared on a session switch: an elicitation id belongs to one prompt forever.
   */
  const answeredElicitationIdsRef = useRef<Set<string>>(new Set());
  const reportedSessionLoadErrorIdRef = useRef<string | null>(null);

  const appendParsedElicitation = useCallback((elicitation: ParsedElicitation) => {
    const elicitationId = elicitation.elicitation?.id;

    // A payload `parseElicitation` could not read, and has already reported. Nothing to answer.
    if (!elicitationId) {
      return;
    }

    if (answeredElicitationIdsRef.current.has(elicitationId)) {
      return;
    }

    // Already on screen. A refetch of the same prompt must not drop the follow-up loader.
    if (pendingElicitationIdsRef.current.has(elicitationId)) {
      return;
    }

    pendingElicitationIdsRef.current.add(elicitationId);
    setPendingElicitations((current) =>
      current.some((entry) => entry.elicitation?.id === elicitationId) ? current : [...current, elicitation],
    );
    // A newly arrived prompt is the server's next state, so the client loader can stand down.
    setElicitationFollowUp(null);
  }, []);

  // A prompt raised before the panel was watching — the tab was reloaded, or the conversation was
  // opened from the session list — is announced by nothing else, its socket event having fired long
  // ago. Skipping it would leave the agent blocked with nothing on screen to unblock it.
  useEffect(() => {
    for (const pending of sessionQuery.data?.pendingElicitations ?? []) {
      appendParsedElicitation(classifyElicitation(pending));
    }
  }, [appendParsedElicitation, sessionQuery.data]);

  useEffect(() => {
    if (!sessionId) {
      reportedSessionLoadErrorIdRef.current = null;
      return;
    }

    if (!sessionQuery.isError || reportedSessionLoadErrorIdRef.current === sessionId) {
      return;
    }

    reportedSessionLoadErrorIdRef.current = sessionId;
    onSessionLoadError(sessionQuery.error);
  }, [onSessionLoadError, sessionId, sessionQuery.error, sessionQuery.isError]);

  const refreshPendingElicitations = useCallback(() => {
    if (!sessionId) {
      return;
    }

    // The same id the query above was keyed on, so the key built here cannot drift from it.
    void queryClient.invalidateQueries({
      queryKey: getPrivateWorkspaceAgentSessionGetKey({ sessionId, workspaceId }),
    });
  }, [queryClient, sessionId, workspaceId]);

  const removeElicitation = useCallback((elicitationId: string) => {
    // Reached when the prompt was answered elsewhere, which the server may not have recorded by the
    // time this session is read back either.
    answeredElicitationIdsRef.current.add(elicitationId);
    pendingElicitationIdsRef.current.delete(elicitationId);
    setPendingElicitations((current) => current.filter((entry) => entry.elicitation?.id !== elicitationId));
  }, []);

  const clearElicitationFollowUp = useCallback(() => {
    setElicitationFollowUp(null);
  }, []);

  const noteElicitationFollowUpSawSessionActivity = useCallback(() => {
    setElicitationFollowUp((current) =>
      !current || current.sawSessionActivity ? current : { ...current, sawSessionActivity: true },
    );
  }, []);

  const getConsentRememberForSession = useCallback(
    (toolName: string) =>
      consentRememberForSessionByToolNameRef.current[toolName] ?? DEFAULT_CONSENT_REMEMBER_FOR_SESSION,
    [],
  );

  const setConsentRememberForSession = useCallback((toolName: string, value: boolean) => {
    consentRememberForSessionByToolNameRef.current[toolName] = value;
  }, []);

  const clearElicitations = useCallback(() => {
    setPendingElicitations([]);
    pendingElicitationIdsRef.current = new Set();
    setElicitationFollowUp(null);
    // "For this session" — a new conversation starts from the default again for every tool.
    consentRememberForSessionByToolNameRef.current = {};
  }, []);

  const respondToElicitation = useCallback(
    async ({
      customText,
      elicitationId,
      fieldValues,
      modifiedArguments,
      numberValue,
      rememberForSession,
      response,
      selectedIndexes,
    }: RespondToElicitationParams) => {
      const activeSessionId = getActiveSessionId();

      if (!activeSessionId) {
        return;
      }

      // Read before the list is updated rather than from inside the updater, which React is free to
      // run after the request has already settled — leaving the failure path with nothing to restore.
      const respondedTo = pendingElicitations.find((entry) => entry.elicitation?.id === elicitationId);

      // Marked answered before the request rather than after it, so a session refetch landing while
      // the response is still in flight cannot seed the card back from data that predates it.
      answeredElicitationIdsRef.current.add(elicitationId);
      pendingElicitationIdsRef.current.delete(elicitationId);

      setPendingElicitations((current) => current.filter((entry) => entry.elicitation?.id !== elicitationId));
      setElicitationFollowUp({
        assistantMessageIds: new Set(
          getMessagesSnapshot().flatMap((message) => (message.role === 'assistant' ? [message.id] : [])),
        ),
        sawSessionActivity: false,
      });

      try {
        const request: ElicitationRespondRequest = {
          agentType: 'workspace',
          elicitationId,
          response,
          sessionId: activeSessionId,
          workspaceId,
          ...(rememberForSession !== undefined ? { rememberForSession } : {}),
          ...(modifiedArguments ? { modifiedArguments } : {}),
          ...(selectedIndexes ? { selectedIndexes } : {}),
          ...(customText ? { customText } : {}),
          ...(fieldValues ? { fieldValues } : {}),
          ...(numberValue !== undefined ? { numberValue } : {}),
        };
        await respondToElicitationRequest(request);
      } catch (caught) {
        // The agent is still blocked on this decision, so the prompt has to be answerable again.
        // Unconditional, since an elicitation id belongs to one prompt forever: dropping it is what
        // lets a later refetch of that session seed the card back.
        answeredElicitationIdsRef.current.delete(elicitationId);
        setElicitationFollowUp(null);

        // Answered in a conversation the panel has since left. Its card, and its error, belong to
        // that conversation rather than to whichever one is now on screen.
        if (getActiveSessionId() !== activeSessionId) {
          return;
        }

        onError(getErrorMessage(caught));

        if (respondedTo) {
          appendParsedElicitation(respondedTo);
        }
      }
    },
    [
      appendParsedElicitation,
      getActiveSessionId,
      getMessagesSnapshot,
      onError,
      pendingElicitations,
      respondToElicitationRequest,
      workspaceId,
    ],
  );

  return {
    appendParsedElicitation,
    clearElicitationFollowUp,
    clearElicitations,
    elicitationFollowUpAssistantMessageIds: elicitationFollowUp?.assistantMessageIds ?? null,
    elicitationFollowUpSawSessionActivity: elicitationFollowUp?.sawSessionActivity ?? false,
    noteElicitationFollowUpSawSessionActivity,
    getConsentRememberForSession,
    isAwaitingElicitationFollowUp,
    pendingElicitations,
    refreshPendingElicitations,
    removeElicitation,
    respondToElicitation,
    setConsentRememberForSession,
  };
};
